import { isArray } from 'lodash'
import Vue from 'vue'
import * as services from '../../app/auth/services.js'
import { setToken } from './index.js'
import {setColorCode} from '../../utils/setColor.js'
import { POST_LOGIN_REDIRECT_KEY, safePostLoginRedirect } from '../../utils/lmsRedirect'

let systemAlertRefreshTimer = null

/**
 * Nudge the System Alerts bell after anything that saves.
 *
 * Saving a setting makes the server re-run the matching health check, so the
 * answer on screen is out of date the moment the save succeeds — and the bell
 * only polls every five minutes. Rather than wiring a refresh into each of the
 * settings screens, it is triggered here: every save in the product goes
 * through this interceptor exactly once.
 *
 * One fetch, and a delayed one. The server runs its check after the response
 * has been sent — probing an SMTP host or the Mailgun API takes a few seconds —
 * so refetching the moment the save returns reads the state from before the
 * check finished. Waiting once is both correct and quieter than asking twice
 * and showing the first, wrong answer for six seconds.
 */
function signalSystemAlertRefresh(config) {
    if (!config || !config.method) {
        return
    }

    const method = String(config.method).toLowerCase()

    if (method === 'get' || method === 'head' || method === 'options') {
        return
    }

    // The bell's own actions already refetch, and re-entering here would have
    // them do it twice.
    //
    // Matched at the start of the path, not anywhere in it. The bell's
    // endpoints sit at the API root (/system-alerts/summary, /dismiss-all and
    // so on) while the settings screen posts to /setting/system-alerts — and a
    // plain "contains" test caught that too, so saving the System Alerts
    // settings was the one request that could never refresh the bell it had
    // just changed.
    const path = String(config.url || '').split('?')[0]

    if (path === '/system-alerts' || path.indexOf('/system-alerts/') === 0) {
        return
    }

    const bus = Vue.prototype.$bus

    if (!bus) {
        return
    }

    // One form save can fire several requests; coalesce them, and restart the
    // wait each time so the fetch lands after the last one, not the first.
    if (systemAlertRefreshTimer) {
        clearTimeout(systemAlertRefreshTimer)
    }

    // Long enough for the server's own re-check to have finished. The slowest
    // of them — Mailgun, two API calls plus an SMTP probe — measured around
    // five seconds.
    systemAlertRefreshTimer = setTimeout(() => {
        systemAlertRefreshTimer = null
        bus.$emit('system-alerts-refresh')
    }, 7000)
}


export default (http, store, router) => {
    // Add a request interceptor
    http.interceptors.request.use(
        async config => {
            setColorCode()
            if (! config.hideLoader ) {
                // Show loader
                $('.pace').removeClass('pace-inactive');
                $('.pace').addClass('pace-active');
            }
            if(config.url.includes('/auth_token/revoke')){
                config.headers['Clear-Site-Data'] = '"cache", "cookies", "storage"';
            }
            if (typeof window !== 'undefined' && window.location) {
                const currentPath = window.location.pathname || '/';
                const fullUrl = window.location.origin + currentPath;
                config.headers['X-Page-URL'] = fullUrl;
            }
            // Ensure token exists before making request
            let token = store.state.Auth && store.state.Auth.token;
            if (!token) {
                // Try localStorage
                token = localStorage.getItem('auth_token');
            }

            if (!token && !config.url.includes('/auth/login') && !config.url.includes('/auth/verify') && !config.url.includes('/public/')) {
                try {
                    await store.dispatch('checkUserToken');
                    token = store.state.Auth && store.state.Auth.token;
                } catch (e) {
                    console.warn('Token check failed', e);
                }
            }

            if (token) {
                config.headers['Authorization'] = 'Bearer ' + token;
            }

            store.dispatch('setFetching', { fetching: true });
            return config;
        },
        (error) => Promise.reject(error)
    );

    // https://github.com/mzabriskie/axios#interceptors
    http.interceptors.response.use(

        response => {
            if (! response.config.hideLoader ) {
                // Remove loader
                $('.pace').removeClass('pace-active');
                $('.pace').addClass('pace-inactive');
            }

            store.dispatch('setFetching', { fetching: false })

            signalSystemAlertRefresh(response.config)

            return response
        },

        /**
        * This is a central point to handle all
        * error messages generated by HTTP
        * requests
        */
        (error) => {

            if (! error.config.hideLoader ) {
                // Remove loader
                $('.pace').removeClass('pace-active');
                $('.pace').addClass('pace-inactive');
            }

            const { response } = error

            /**
            * If token is either expired, not provided or invalid
            * then redirect to login. On server side the error
            * messages can be changed on app/Providers/EventServiceProvider.php
            */
            if (response.status == 401) {
                // Approver deep-link opened with a STALE token: the route guard let it
                // through, then the preview's API 401'd here. Remember where the approver
                // was headed (campaign/announcement/policy preview) so the admin login
                // returns them there. sessionStorage survives the clear() below.
                try {
                    var intended401 = (router.currentRoute && router.currentRoute.fullPath) || '';
                    if (safePostLoginRedirect(intended401)) {
                        sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, intended401);
                    }
                } catch (e) { /* noop */ }
                store.dispatch('setToken', null)
                store.dispatch('setUser', {})
                localStorage.clear();
                const base64String = 'WW91ciBzZXNzaW9uIGhhcyBiZWVuIGV4cGlyZWQuIFBsZWFzZSBsb2dpbiBhZ2Fpbi4=';
                if (router.currentRoute.path.includes('/userportal-v2') || router.currentRoute.path.includes('/userportal')) {
                    router.push({ name: 'auth.userportalLogin', query: { errorCode: base64String } });
                } else {
                    router.push({ name: 'auth.login', query: { errorCode: base64String } });
                }
            }

            store.dispatch('resetMessages')

            if (response && response.data) {
                if (response.data.errors && typeof response.data.errors === 'object') {
                    store.dispatch('setMessage', { type: 'error', message: response.data.errors });
                } else if (response.data.message) {
                    store.dispatch('setMessage', { type: 'warning', message: response.data.message });
                }
            }



            /**
             * Laravel generated validation errors are
             * sent in an object
             */
            // if (response.data.message) {
            //     store.dispatch('setMessage', { type: 'validation', message: response.data.message })
            // }

            store.dispatch('setFetching', { fetching: false })

            return Promise.reject(error)
        }
    )
}



// WEBPACK FOOTER //
// ./src/plugins/http/interceptors.js